import { useState } from "react";

import config from "../../config";

import useDebouncedCallback from "./use-debounced-callback";
import useOnUnmount from "./use-on-unmount";

const useDoubleClick = <T = HTMLElement>(
  onDoubleClick?: React.MouseEventHandler<T>,
  onClick?: React.MouseEventHandler<T>,
  onBlur?: React.FocusEventHandler<T>,
  doubleClickTimeout = config.shared.doubleClickTimeout
): {
  waitingForDoubleClick: boolean;
  doubleClicked: boolean;
  onClick: React.MouseEventHandler<T>;
  onBlur: React.FocusEventHandler<T>;
} => {
  const [waitingForDoubleClick, setWaitingForDoubleClick] = useState(false);
  const [doubleClicked, setDoubleClicked] = useState(false);

  const onDebouncedClick = useDebouncedCallback((event: React.MouseEvent<T>) => {
    setWaitingForDoubleClick(false);
    setDoubleClicked(false);
    onClick?.(event);
  }, doubleClickTimeout);

  useOnUnmount(() => {
    onDebouncedClick.cancel();
  });

  return {
    waitingForDoubleClick,
    doubleClicked,
    onClick: (event: React.MouseEvent<T>) => {
      if (onDoubleClick) {
        if (waitingForDoubleClick) {
          onDebouncedClick.cancel();
          onDoubleClick(event);
          setWaitingForDoubleClick(false);
          setDoubleClicked(true);
        } else {
          setWaitingForDoubleClick(true);
          onDebouncedClick(event);
        }
      } else {
        onDebouncedClick.cancel();
        onClick?.(event);
      }
    },
    onBlur: (event: React.FocusEvent<T>) => {
      setWaitingForDoubleClick(false);
      setDoubleClicked(false);
      onBlur?.(event);
    }
  };
};

export default useDoubleClick;
